import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { setCacheVersion } from '@src/utilities/cache-version-utils'

import firestore from './firebase-db'

const DEVSUITE_COLLECTION = 'devsuite'
const CACHE_VERSION_DOCUMENT_ID = 'general'

export interface CacheVersionGeneral {
  history: string[]
  version: string
}

const CACHE_VERSION_DEFAULT: CacheVersionGeneral = {
  history: [],
  version: '',
}

const getDocRef = (projectName: string) =>
  doc(
    firestore,
    DEVSUITE_COLLECTION,
    projectName,
    'cache-version',
    CACHE_VERSION_DOCUMENT_ID
  )

export const fetchCacheVersionFromFirebase = async (
  projectName: string
): Promise<string> => {
  if (!firestore || !projectName) {
    return ''
  }

  try {
    const snapshot = await getDoc(getDocRef(projectName))
    if (snapshot.exists()) {
      const data = snapshot.data() as CacheVersionGeneral
      return data?.version ?? ''
    }
  } catch (error) {
    console.error('fetchCacheVersionFromFirebase error', error)
  }
  return ''
}

const useFirebaseCacheVersionService = (
  projectName: string,
  enabled: boolean
) => {
  const [cacheVersionData, setCacheVersionData] =
    useState<CacheVersionGeneral>()

  useEffect(() => {
    if (!enabled || !projectName) {
      setCacheVersionData(undefined)
      return () => {}
    }

    const docRef = getDocRef(projectName)
    const unsub = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as CacheVersionGeneral
          setCacheVersionData({
            history: data?.history ?? [],
            version: data?.version ?? '',
          })
          if (data?.version != null) {
            setCacheVersion(data.version)
          }
        } else {
          setCacheVersionData(CACHE_VERSION_DEFAULT)
        }
      },
      () => {
        setCacheVersionData(CACHE_VERSION_DEFAULT)
      }
    )

    return () => {
      if (unsub) {
        unsub()
      }
    }
  }, [projectName, enabled])

  return {
    cacheVersionData,
  }
}

export default useFirebaseCacheVersionService
